import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { WithIcon, WithTooltip } from "shared/table-helpers";
import * as notify from "shared/notify";
import { format } from "date-fns";
import { DATE_FNS_LONG_DATE_FORMAT } from "shared/constants";
import { Select } from "components/form-inputs/inputs/select";
import { inputFilter } from "components/task-list/lib/constants";
import { InlineCellEditor } from "../../../components/form-inputs/inputs";

export const DEFAULT_USER = {
    createdAt: "",
    email: "",
    firstName: "",
    fullName: "",
    lastLogin: "",
    lastName: "",
    userId: "",
    userAssignmentGroups: [],
};

export const filterConfig = {
    name: "contains",
    description: "contains",
    firstName: "contains",
    lastName: "contains",
    email: "contains",
};

export const createUserStatusOptions = () => {
    return [
        {
            value: "1",
            text: "Active",
        },
        {
            value: "0",
            text: "Disabled",
        },
    ];
};

export const getFilterParams = (filters) => {
    const paramFiltered = filters.map((filter) => {
        return `&filters=${filter.id}:${filter.value}`;
    });

    return paramFiltered.length > 0 ? paramFiltered.join("") : "";
};

export const buildColumns = (deleteById) => {
    return [
        {
            Header: WithTooltip("Name"),
            accessor: "name",
        },
        {
            Header: WithTooltip("Description"),
            accessor: "description",
            style: { whiteSpace: "unset" },
        },
        {
            Header: WithIcon("Remove", faTrash),
            accessor: "id",
            filterable: false,
            sortable: false,
            width: 40,
            Cell: (row) => {
                return (
                    <button
                        className="button -text"
                        title="Remove"
                        onClick={() => {
                            deleteById(row.value);
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                );
            },
        },
    ];
};

export const buildTeamColumns = (deleteById) => {
    return [
        {
            Header: WithTooltip("Name"),
            accessor: "name",
        },
        {
            Header: WithIcon("Remove", faTrash),
            accessor: "key",
            filterable: false,
            sortable: false,
            width: 40,
            Cell: (row) => {
                return (
                    <button
                        className="button -text"
                        title="Remove"
                        onClick={() => {
                            deleteById(row.value);
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                );
            },
        },
    ];
};

export const buildPermissions = (deleteById, disableDelete) => {
    return [
        {
            Header: WithTooltip("Name"),
            accessor: "name",
        },
        {
            Header: WithTooltip("Description"),
            accessor: "description",
            style: { whiteSpace: "unset" },
        },
        {
            Header: WithIcon("Remove", faTrash),
            accessor: "name",
            filterable: false,
            sortable: false,
            width: 40,
            Cell: (row) => {
                return (
                    <button
                        className="button -text"
                        title="Remove"
                        disabled={disableDelete}
                        onClick={async () => {
                            const shouldDelete = await notify.confirm(
                                "Are you sure you would like to remove this permission from this role?<br /><br />Please contact the development team if you have any question(s) before doing so."
                            );

                            if (shouldDelete) {
                                deleteById(row.value);
                            } else {
                                return false;
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                );
            },
        },
    ];
};

export const buildUserColumns = () => {
    return [
        {
            Header: WithTooltip("First Name"),
            accessor: "firstName",
            filterable: true,
            Filter: inputFilter,
        },
        {
            Header: WithTooltip("Last Name"),
            accessor: "lastName",
            filterable: true,
            Filter: inputFilter,
        },
        {
            Header: WithTooltip("Email"),
            accessor: "email",
            filterable: true,
            Filter: inputFilter,
        },
        {
            Header: WithTooltip("Team"),
            accessor: "teams",
            filterable: true,
            Filter: inputFilter,
            sortable: false,
            Cell: (row) => {
                const teams = row.value.map(({ name }) => name).join("; ");

                return WithTooltip(teams);
            },
        },
        {
            Header: WithTooltip("Role"),
            accessor: "roles",
            filterable: true,
            sortable: false,
            Filter: inputFilter,
            Cell: (row) => {
                const roles = row.value.map(({ name }) => name).join("; ");

                return WithTooltip(roles);
            },
        },
        {
            Header: WithTooltip("Status"),
            accessor: "isActive",
            filterable: true,
            Cell: (row) => {
                const status = row.value ? "Active" : "Inactive";

                return WithTooltip(status);
            },
            Filter: ({ filter, onChange }) => {
                return (
                    <Select
                        onChange={(event) => onChange(event.target.value)}
                        value={filter && filter.value}
                        options={createUserStatusOptions()}
                    />
                );
            },
        },
        {
            Header: WithTooltip("Last Login"),
            accessor: "lastLogin",
            filterable: false,
            Filter: inputFilter,
            Cell: (row) => {
                const dateString = row.value ? format(new Date(row.value), DATE_FNS_LONG_DATE_FORMAT) : "";

                return WithTooltip(dateString);
            },
        },
    ];
};

export const COLUMN_PERMISSIONS = [
    {
        Header: WithTooltip("Name"),
        accessor: "name",
    },
    {
        Header: WithTooltip("Description"),
        accessor: "description",
        style: { whiteSpace: "unset" },
    },
];

export const buildSlaGroups = (updateCell, isUpdating, setPagingDisabled) => [
    {
        Header: WithTooltip("Group Name"),
        accessor: "shortName",
        Filter: inputFilter,
        filterable: true,
        disabled: isUpdating,
        Cell: (row) => {
            return (
                <InlineCellEditor
                    field="shortName"
                    value={row.value}
                    rowData={row.original}
                    onSubmit={updateCell}
                    onEditStart={() => setPagingDisabled(true)}
                    onEditEnd={() => setPagingDisabled(false)}
                />
            );
        },
    },
    {
        Header: WithTooltip("Description"),
        accessor: "description",
        filterable: false,
        Cell: (row) => {
            return (
                <InlineCellEditor
                    field="description"
                    value={row.value}
                    rowData={row.original}
                    onSubmit={updateCell}
                    onEditStart={() => setPagingDisabled(true)}
                    onEditEnd={() => setPagingDisabled(false)}
                />
            );
        },
    },
    {
        Header: WithTooltip("Clients"),
        accessor: "clientSlas",
        sortable: false,
        filterable: false,
        Cell: (row) => {
            return WithTooltip(row.value.map((clientSla) => clientSla.clientName).join(", "));
        },
        style: {
            backgroundColor: "#EDEDEE",
        },
    },
];
